import React, { FC, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import clsx from 'clsx';
import { formatAmount, isDefined, isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  LabelValue,
  List,
  ListItem,
  Box,
  CardGeneric,
  CardHeading,
  Typography,
  makeStyles,
  VopStatus,
} from '@dxb-web-ui-framework/design-system';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { invokeService, useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { authorizationService } from '../../../../../channels/secure-web/services';
import { useCorporateCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { ResidentBankLogo } from '../../../../transaction/src/components/TransactionDetails/ResidentBankLogo';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';
import { CheckMandateResponseBody } from '../../../../authorizations/src/domain/Authorization/entities/CheckMandateResponseDTO';
import {
  useWizardGoToNextStep,
  useWizardGoToPreviousStep,
} from '../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { ECOMMBXLoader } from '../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { ErrorAlert } from '../../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import { ContentWrapper } from '../../../../content-composer/src/components/Generic/ContentWrapper';
import { TransferServiceImpl } from '../../domain/Transfer/usecases';
import { InitiateTransferResponseBody } from '../../domain/Transfer/entities/InitiateTransferResponseDTO';
import { SaveFavouriteBeneficiaryButton } from '../SaveFavouriteBeneficiaryButton';
import { VopResponseBody } from '../../domain/Transfer/entities/VopResponseDTO';
import { RoutingTransferTypes } from '../../domain/Transfer/entities/RoutingResponseDTO';
import { shouldShowRequestRateBtn } from '../../utils';
import { StepperActions, ExchangeRatesInfo, useFundsTransferUILogicDisplayer, RequestDealerRate } from './';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
  },
  subContainer: {
    flexFlow: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  fullHeight: {
    height: '100%',
  },
  subItemContainer: {
    flexFlow: 'column',
    display: 'flex',
  },
  defaultBackgroundColor: {
    backgroundColor: theme.palette.background.default,
  },
  list: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0),
    },
  },
  listItem: {
    wordBreak: 'break-all',
    alignItems: 'flex-start',
    display: 'inline-block',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  oneColumn: {
    columnCount: 1,
  },
  twoColumns: {
    columnCount: 2,
  },
  fromToAccountContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    overflowWrap: 'break-word',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
}));

type TransferConfirmProps = {
  service: TransferServiceImpl;
  isSaveBeneficiaryButtonEnabled: boolean;
  externalEffectCallbacks: {
    onBack: () => void;
    onNext: () => void;
  };
};

const TransferConfirm: FC<TransferConfirmProps> = ({
  externalEffectCallbacks,
  isSaveBeneficiaryButtonEnabled,
  service,
}) => {
  const [isPostingData, setIsPostingData] = useState(false);
  const [postDataError, setPostDataError] =
    useState<AxiosError<APIGatewayResponse<InitiateTransferResponseBody>, any> | undefined>(undefined);

  const [vopStatus, setVopStatus] =
    useState<Nullable<'EXACT_MATCH' | 'CLOSE_MATCH' | 'NO_MATCH' | 'NOT_COMPLETED'>>(null);
  const [vopTimestamp, setVopTimestamp] = useState<Nullable<String>>(null);
  // hooks
  const { t } = useTranslation();
  const language = useLanguage();

  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  const goToNextWizardStep = useWizardGoToNextStep();
  const goToPreviousWizardStep = useWizardGoToPreviousStep();

  const classes = useStyles();

  //UI logic rendering by Transfer type
  const {
    forecastFormData,
    forecastResponseData,
    otherMandatoryDataForUI,
    routingResponseData,
    routingFormData,
    shouldShowBankName,
    shouldShowBankBIC,
    shouldShowBeneficiaryCountry,
    shouldShowBeneficiaryAddress,
    shouldShowExchangeRates,
    isOneColumnGrid,
    creditCurrency,
    debitCurrency,
    shouldConsiderForecastFormData,
    formatAmountHelper,
    renderBeneficiaryBankName,
    selectedBankSwiftCode,
    bankCountry,
    isExternalTransferType,
    isDebitCurrencySameWithOrderCurrency,
    transferType,
  } = useFundsTransferUILogicDisplayer('initTransfer');

  // callbacks
  const onClickNext = async (hasUserSelectedDealerRate = false) => {
    setIsPostingData(true);
    setPostDataError(undefined);
    const { error } = await invokeService<InitiateTransferResponseBody>(() => {
      const requestObject = {
        appTransactionUniqueId: forecastFormData.appTransactionUniqueId,
        dealerRateRequest: hasUserSelectedDealerRate,
      };
      return service.Initiate(requestObject, (response: GenericObject) => {
        goToNextWizardStep({
          forecastFormData,
          forecastResponseData,
          otherMandatoryDataForUI,
          routingResponseData,
          routingFormData,
          ...response.data,
          hasUserSelectedDealerRate,
          onCancel: goToPreviousWizardStep,
        });
      });
    });
    setIsPostingData(false);
    setPostDataError(error);
  };

  const corporateId = useCorporateCustomerId();
  const isCorporate = Boolean(corporateId);

  const { data: checkMandateData, error: checkMandateError } = useInvokeSWRService<CheckMandateResponseBody>(
    authorizationService.CheckMandate,
    {
      accountNumber: routingFormData.debitAccountNumber,
      uniqueId: forecastFormData.appTransactionUniqueId,
      amount: routingFormData.orderAmount,
    },
    isCorporate,
    'transferService.GetSwiftCodesByBic',
  );

  const onRequestDealerUserSelection: React.MouseEventHandler<HTMLButtonElement> = () => {
    onClickNext(true);
  };

  const onConfirmWithoutDealerRate = () => {
    onClickNext(false);
  };

  const bankCountryRenderer = () => {
    if (shouldConsiderForecastFormData) {
      if (typeof forecastFormData.receiverCountry === 'string') {
        return forecastFormData.receiverCountry;
      } else {
        return forecastFormData.receiverCountry.label;
      }
    }
    return routingResponseData.bankName;
  };

  const error = postDataError || checkMandateError;
  const shouldShowIndividualRequestRateButton =
    !isCorporate &&
    shouldShowRequestRateBtn({
      transferType,
      isDebitCurrencySameWithOrderCurrency,
      isEligibleForDealerRate: !!forecastResponseData.isEligibleForDealerRate,
      isLastAuthorizer: true,
    });

  const shouldShowCorporateRequestRateButton = Boolean(
    checkMandateData &&
      (checkMandateData?.lastAuthorizer === true || checkMandateData?.lastAuthorizer === false) &&
      checkMandateData?.needMandate === false &&
      isCorporate &&
      shouldShowRequestRateBtn({
        transferType,
        isDebitCurrencySameWithOrderCurrency,
        isEligibleForDealerRate: !!forecastResponseData.isEligibleForDealerRate,
        isLastAuthorizer: !!checkMandateData?.lastAuthorizer,
      }),
  );

  const getBeneficiaryName = () => {
    const shouldShowBeneficiaryNameAsUnMasked =
      [
        RoutingTransferTypes.INTERNAL_SAME_CURRENCY,
        RoutingTransferTypes.INTERNAL_OTHER_CURRENCY,
        RoutingTransferTypes.INTRABANK_SAME_CURRENCY,
        RoutingTransferTypes.INTRABANK_OTHER_CURRENCY,
        RoutingTransferTypes.CORPORATE_INTERNAL_SAME_CURRENCY,
        RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY,
      ].includes(routingResponseData.transferType) && routingResponseData?.ecommbx;
    if (shouldShowBeneficiaryNameAsUnMasked) {
      return routingResponseData.creditorFullNameUnMasked;
    } else {
      return shouldConsiderForecastFormData ? forecastFormData.receiverFullName : routingResponseData.creditorFullName;
    }
  };

  const {
    data: vopData,
    error: vopError,
    isLoading: isLoadingVop,
  } = useInvokeSWRService<VopResponseBody>(
    service.Vop,
    {
      bic: shouldConsiderForecastFormData ? forecastFormData.receiverBic : routingResponseData.bic,
      name: getBeneficiaryName(),
      iban: routingResponseData.iban,
      meta: { timestamp: vopTimestamp },
    },
    !!vopTimestamp,
  );

  useEffect(() => {
    if (forecastResponseData && forecastResponseData?.vopStatus) {
      setVopStatus(forecastResponseData.vopStatus);
    }
    if (vopData) {
      setVopStatus(vopData.vopStatus);
    }
  }, [forecastResponseData, vopData]);

  const vopStatusInfo = {
    EXACT_MATCH: {
      title: 'EXACT MATCH',
      description: t('fundsTransferPage.TransferStepper.TransferConfirm.Vop.ExactMatch.Text'),
      onClick: undefined,
    },
    CLOSE_MATCH: {
      title: 'CLOSE MATCH',
      description: t('fundsTransferPage.TransferStepper.TransferConfirm.Vop.CloseMatch.Text'),
      onClick: undefined,
    },
    NO_MATCH: {
      title: 'NO MATCH',
      description: t('fundsTransferPage.TransferStepper.TransferConfirm.Vop.NoMatch.Text'),
      onClick: undefined,
    },
    NOT_COMPLETED: {
      title: 'NOT COMPLETED',
      description: t('fundsTransferPage.TransferStepper.TransferConfirm.Vop.NotCompleted.Text'),
      onClick: () => {
        setVopTimestamp(new Date().toISOString());
      },
    },
  };

  return (
    <ContentWrapper
      hasDivider={false}
      boxProps={{
        paddingLeft: 3,
        paddingRight: 3,
        paddingTop: 4,
        boxShadow: '0 3px 20px #00000033',
        height: '100%',
      }}
    >
      <Grid container justifyContent='flex-start'>
        <Grid item xs={12}>
          <Typography variant='subtitle1' color='primary'>
            {t('fundsTransferPage.TransferStepper.TransferConfirm.Title.Text')}
          </Typography>
          {error && (
            <Box mt={3}>
              <ErrorAlert message={error.message} />
            </Box>
          )}
        </Grid>
        <Grid container className={classes.subContainer}>
          <Grid item xs={isMobile ? 12 : 6} className={classes.subItemContainer}>
            <Box pt={3} pb={5} px={3} mt={4} className={clsx(classes.defaultBackgroundColor, classes.fullHeight)}>
              <Typography variant='h4'>{formatAmountHelper(routingFormData.orderAmount)}</Typography>
              <ExchangeRatesInfo
                t={t}
                isMobile={isMobile}
                accountCurrency={debitCurrency.shortDescription}
                rateCurrencyISO={forecastResponseData.rateCurrencyISO}
                exchangeCurrency={creditCurrency.shortDescription}
                initialAmountValue={routingFormData.orderAmount}
                exchangeRate={forecastResponseData.currentOrderRate}
                exchangeAmountValue={
                  isDebitCurrencySameWithOrderCurrency
                    ? forecastResponseData.outCreditAmount
                    : forecastResponseData.currentOrderDebitedAmount
                }
                exchangeAmountCurrency={
                  isDebitCurrencySameWithOrderCurrency
                    ? creditCurrency.shortDescription
                    : debitCurrency.shortDescription
                }
                isLoading={false}
                shouldShowExchangeRates={shouldShowExchangeRates}
              >
                {(shouldShowCorporateRequestRateButton || shouldShowIndividualRequestRateButton) && (
                  <RequestDealerRate
                    t={t}
                    onRequestDealerUserSelection={onRequestDealerUserSelection}
                    isLoading={isPostingData}
                  />
                )}
              </ExchangeRatesInfo>
              <Box mt={isMobile ? 3 : 6}>
                <CardHeading heading={t('fundsTransferPage.TransferFlow.From.Text')} hideOnMobile={false}>
                  <CardGeneric spacing='dense'>
                    <Box component={'span'} className={classes.fromToAccountContainer}>
                      <LabelValue
                        label={
                          otherMandatoryDataForUI.fromAccount?.alias ||
                          otherMandatoryDataForUI.fromAccount?.productDescription
                        }
                        labelTestId='funds-transfer-account-name-from'
                        value={otherMandatoryDataForUI.fromAccount?.iban}
                        valueTestId='funds-transfer-account-number-from'
                        valueAreaLabel={`${otherMandatoryDataForUI.fromAccount?.alias} ${otherMandatoryDataForUI.fromAccount?.productDescription} ${otherMandatoryDataForUI.fromAccount?.iban} ${otherMandatoryDataForUI.fromAccount?.accountNumber}`}
                        labelColor='primary'
                        labelSize='small'
                        valueSize={isMobile ? 'small' : 'medium'}
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                      />
                      <Box ml={1} mt={1}>
                        <ResidentBankLogo resident='EcommBX' />
                      </Box>
                    </Box>
                  </CardGeneric>
                </CardHeading>
              </Box>
              <Box mt={3}>
                <CardHeading heading={t('fundsTransferPage.TransferFlow.To.Text')} hideOnMobile={false}>
                  <CardGeneric spacing='dense'>
                    <Box component={'span'} className={classes.fromToAccountContainer}>
                      <LabelValue
                        label={getBeneficiaryName()}
                        labelTestId='funds-transfer-account-name-to'
                        value={routingResponseData.iban}
                        valueTestId='funds-transfer-account-number-to'
                        valueAreaLabel={routingResponseData.iban}
                        labelColor='primary'
                        labelSize='small'
                        valueSize={isMobile ? 'small' : 'medium'}
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                      />
                      <Box ml={1} mt={1}>
                        {renderBeneficiaryBankName()}
                      </Box>
                    </Box>
                  </CardGeneric>
                </CardHeading>
              </Box>
              <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                {isSaveBeneficiaryButtonEnabled && (
                  <Box>
                    <SaveFavouriteBeneficiaryButton
                      routingResponseData={routingResponseData}
                      foreCastFormData={forecastFormData}
                      selectedBankSwiftCode={selectedBankSwiftCode}
                      isLoading={isPostingData}
                      isExternalTransferType={isExternalTransferType}
                    />
                  </Box>
                )}
                {!!vopStatus && !vopError && !isLoadingVop && (
                  <Box mt={2}>
                    <VopStatus
                      title={vopStatusInfo[vopStatus].title}
                      variant={vopStatus}
                      helperTitle={t('fundsTransferPage.TransferStepper.TransferConfirm.Vop.HelperText.Text')}
                      description={vopStatusInfo[vopStatus].description}
                      onClick={vopStatusInfo[vopStatus]?.onClick}
                      tooltip={t('fundsTransferPage.TransferStepper.TransferConfirm.Vop.ToolTip.Text')}
                    />
                  </Box>
                )}
                {isLoadingVop && (
                  <Box mt={2}>
                    <ECOMMBXLoader size='small' verticalSpacing='small' />
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={isMobile ? 12 : 6} className={classes.subItemContainer}>
            <List
              className={clsx(classes.list, {
                [classes.oneColumn]: isOneColumnGrid,
                [classes.twoColumns]: !isOneColumnGrid && !isMobile,
              })}>
              <ListItem className={classes.listItem}>
                <LabelValue
                  label={t('fundsTransferPage.TransferFlow.Currency')}
                  value={debitCurrency.shortDescription}
                  valueAreaLabel={debitCurrency.shortDescription}
                  labelColor='textPrimary'
                  labelSize='small'
                  valueSize='small'
                  valueColor='secondary'
                  labelAlignment='left'
                  valueAlignment='left'
                />
              </ListItem>
              <ListItem className={classes.listItem}>
                <LabelValue
                  label={t('fundsTransferPage.TransferFlow.Beneficiary.FullName')}
                  value={getBeneficiaryName()}
                  valueAreaLabel={getBeneficiaryName()}
                  labelColor='textPrimary'
                  labelSize='small'
                  valueSize='small'
                  valueColor='secondary'
                  labelAlignment='left'
                  valueAlignment='left'
                />
              </ListItem>
              {shouldShowBeneficiaryAddress && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.BeneficiaryAddress')}
                    value={
                      shouldConsiderForecastFormData
                        ? forecastFormData.receiverAddress
                        : routingResponseData.bankAddress
                    }
                    valueAreaLabel={
                      shouldConsiderForecastFormData
                        ? forecastFormData.receiverAddress
                        : routingResponseData.bankAddress
                    }
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {shouldShowBeneficiaryCountry && shouldConsiderForecastFormData && forecastFormData?.receiverCity && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.BeneficiaryCity')}
                    value={forecastFormData?.receiverCity}
                    valueAreaLabel={forecastFormData?.receiverCity}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {shouldShowBeneficiaryCountry && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.BeneficiaryCountry')}
                    value={bankCountryRenderer()}
                    valueAreaLabel={bankCountryRenderer()}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.chargingMethodDescription && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.Charges')}
                    value={forecastFormData.chargingMethodDescription}
                    valueAreaLabel={forecastFormData.chargingMethodDescription}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {isDefined(forecastResponseData?.commissionAmount) && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.Commission')}
                    value={formatAmount(
                      forecastResponseData.commissionAmount,
                      debitCurrency.shortDescription,
                      2,
                      language.cultureName,
                    )}
                    valueAreaLabel={formatAmount(
                      forecastResponseData.commissionAmount,
                      debitCurrency.shortDescription,
                      2,
                      language.cultureName,
                    )}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {isDefined(forecastResponseData?.totalExpensesAmount) && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.TotalCharges')}
                    value={formatAmount(
                      forecastResponseData.totalExpensesAmount,
                      debitCurrency.shortDescription,
                      2,
                      language.cultureName,
                    )}
                    valueAreaLabel={formatAmount(
                      forecastResponseData.totalExpensesAmount,
                      debitCurrency.shortDescription,
                      2,
                      language.cultureName,
                    )}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {shouldShowBankName && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.BenBankName')}
                    value={shouldConsiderForecastFormData ? forecastFormData.bankName : routingResponseData.bankName}
                    valueAreaLabel={
                      shouldConsiderForecastFormData ? forecastFormData.bankName : routingResponseData.bankName
                    }
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {shouldShowBankBIC && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.BankBIC')}
                    value={shouldConsiderForecastFormData ? forecastFormData.receiverBic : routingResponseData.bic}
                    valueAreaLabel={
                      shouldConsiderForecastFormData ? forecastFormData.receiverBic : routingResponseData.bic
                    }
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {shouldShowBankName && bankCountry && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.BankCountry')}
                    value={bankCountry?.description}
                    valueAreaLabel={bankCountry?.description}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.receiverComments && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.Comments')}
                    value={forecastFormData.receiverComments}
                    valueAreaLabel={forecastFormData.receiverComments}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.bopCode && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.BopCode')}
                    value={forecastFormData.bopCode.label}
                    valueAreaLabel={forecastFormData.bopCode.label}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.receiverSortCode && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.SortCode')}
                    value={forecastFormData.receiverSortCode}
                    valueAreaLabel={forecastFormData.receiverSortCode}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.cor && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.COR')}
                    value={`${forecastFormData.cor}`}
                    valueAreaLabel={`${forecastFormData.cor}`}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.bik && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.BIK')}
                    value={`${forecastFormData.bik}`}
                    valueAreaLabel={`${forecastFormData.bik}`}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.inn && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.INN')}
                    value={`${forecastFormData.inn}`}
                    valueAreaLabel={`${forecastFormData.inn}`}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.kpp && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.KPP')}
                    value={`${forecastFormData.kpp}`}
                    valueAreaLabel={`${forecastFormData.kpp}`}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {forecastFormData?.vo && (
                <ListItem className={classes.listItem}>
                  <LabelValue
                    label={t('fundsTransferPage.TransferFlow.VO')}
                    value={forecastFormData?.vo?.label}
                    valueAreaLabel={forecastFormData?.vo?.label}
                    labelColor='textPrimary'
                    labelSize='small'
                    valueSize='small'
                    valueColor='secondary'
                    labelAlignment='left'
                    valueAlignment='left'
                  />
                </ListItem>
              )}
              {!isEmpty(forecastFormData.uploadedDocs) && (
                <ListItem className={classes.listItem}>
                  <Box display='flex' flexDirection='column'>
                    <LabelValue
                      label={t('fundsTransferPage.TransferFlow.UploadedDocuments')}
                      labelColor='textPrimary'
                      labelSize='small'
                    />
                    {forecastFormData.uploadedDocs.map(({ file }: { file: File }) => (
                      <Box key={file.name} mt={1}>
                        <Typography variant='body2' color='textSecondary'>
                          {file.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </ListItem>
              )}
            </List>
            {isPostingData && (
              <Box mt='auto' mb={isMobile ? 3 : 4} width='100%' display='flex' justifyContent='center'>
                <ECOMMBXLoader size='small' verticalSpacing='small' />
              </Box>
            )}
            <StepperActions
              isMobile={isMobile}
              onClickCancel={externalEffectCallbacks.onBack}
              onClickNext={onConfirmWithoutDealerRate}
              isLoading={isPostingData}
              isConfirmationStep={true}
            />
          </Grid>
        </Grid>
      </Grid>
    </ContentWrapper>
  );
};

export { TransferConfirm };
